// src/redux/slices/profileImageSlice.js (или photoSlice.js — как у тебя в проекте)
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl, getAuthHeaders } from "../../Utils/serverSelector";

// ===========================
// GET /upload/:uid/master
// ===========================
export const fetchMasterPhoto = createAsyncThunk(
  "photo/fetchMasterPhoto",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID not provided");

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error("API server URL is not configured");

      const authHeaders = await getAuthHeaders();

      const response = await axios.get(`${baseUrl}/upload/${uid}/master`, {
        headers: authHeaders,
        withCredentials: true,
        validateStatus: () => true,
      });

      // Нет файла — это норм, возвращаем null
      if (response.status === 404) return null;

      if (response.status === 403) {
        throw new Error(
          response.data?.message || "Forbidden (403) while fetching master photo"
        );
      }

      if (response.status !== 200) {
        throw new Error(
          response.data?.message ||
          `Failed to fetch master photo (HTTP ${response.status})`
        );
      }

      // ожидаем { filePath: "/path/to/file.jpg" }
      return response.data?.filePath || null;
    } catch (error) {
      // если это уже Error из наших throw — берём message
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch master photo";
      return rejectWithValue(msg);
    }
  }
);

// ===========================
// POST /upload  (FormData: uid, file)
// ===========================
export const uploadMasterPhoto = createAsyncThunk(
  "photo/uploadMasterPhoto",
  async ({ uid, file }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID not provided");
      if (!file) throw new Error("File not provided");

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error("API server URL is not configured");

      const formData = new FormData();
      formData.append("uid", uid);
      formData.append("file", file); // File из <input type="file" />

      // ВАЖНО: не задаём вручную Content-Type для multipart
      const uploadHeaders = await getAuthHeaders();
      delete uploadHeaders["Content-Type"];
      delete uploadHeaders["content-type"];

      const response = await axios.post(`${baseUrl}/upload`, formData, {
        headers: uploadHeaders,
        withCredentials: true,
        validateStatus: () => true,
      });

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(
          response.data?.message ||
          `Failed to upload master photo (HTTP ${response.status})`
        );
      }

      // ожидаем { filePath: "/path/to/file.jpg" }
      const filePath = response.data?.filePath || null;
      if (!filePath) throw new Error("Invalid filePath in server response");

      return filePath;
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload master photo";
      return rejectWithValue(msg);
    }
  }
);

// ===========================
// DELETE /upload/:uid/master
// ===========================
export const deleteMasterPhoto = createAsyncThunk(
  "photo/deleteMasterPhoto",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID not provided");

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error("API server URL is not configured");

      const authHeaders = await getAuthHeaders();

      const res = await axios.delete(`${baseUrl}/upload/${uid}/master`, {
        headers: authHeaders,
        withCredentials: true,
        validateStatus: () => true,
      });

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Failed to delete master photo (HTTP ${res.status})`
        );
      }

      return null; // фото удалено
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete master photo";
      return rejectWithValue(msg);
    }
  }
);

// ===========================
// ПУБЛИЧНО: GET /public/:uid/master-photo
// ===========================
export const fetchPublicMasterPhoto = createAsyncThunk(
  "photo/fetchPublicMasterPhoto",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID not provided");

      const base = (getServerUrl() || "").replace(/\/$/, "");
      if (!base) throw new Error("API server URL is not configured");

      const res = await axios.get(`${base}/public/${uid}/master-photo`, {
        validateStatus: () => true,
      });

      if (res.status === 404) return ""; // просто нет фото

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Failed to fetch public master photo (HTTP ${res.status})`
        );
      }

      // ожидаем { filePath: "/uploads/..../file.jpg" } или null
      return res.data?.filePath || "";
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to fetch public master photo";
      return rejectWithValue(msg);
    }
  }
);

// ===========================
// STATE / SLICE
// ===========================
const initialState = {
  photoPath: "", // относительный путь с бэка
  status: "idle",
  error: null,
};

const photoSlice = createSlice({
  name: "photo",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchMasterPhoto.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchMasterPhoto.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.photoPath = action.payload || ""; // null -> ''
      })
      .addCase(fetchMasterPhoto.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message || "error";
      })

      // upload
      .addCase(uploadMasterPhoto.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(uploadMasterPhoto.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.photoPath = action.payload || "";
      })
      .addCase(uploadMasterPhoto.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message || "error";
      })

      // delete
      .addCase(deleteMasterPhoto.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteMasterPhoto.fulfilled, (state) => {
        state.status = "succeeded";
        state.photoPath = "";
      })
      .addCase(deleteMasterPhoto.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message || "error";
      })

      // public
      .addCase(fetchPublicMasterPhoto.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicMasterPhoto.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.photoPath = action.payload || "";
      })
      .addCase(fetchPublicMasterPhoto.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message || "error";
      })

      // reset
      .addCase(resetStore, () => initialState);
  },
});

export default photoSlice.reducer;
